import React, { useState } from 'react'
import PropTypes from 'prop-types'
import useIngestRequest, { channelIdFrom } from './useIngestRequest'
import RefreshArtistConfirm from './RefreshArtistConfirm'
import useAdminAccess from '../../services/hooks/useAdminAccess'
import { ButtonOutline } from '../GeneralComponents/ButtonOutline/ButtonOutline'
import { ModalWindow } from '../GeneralComponents/ModalWindow/ModalWindow'
import successToast from '../Toasts/SuccessToast'
import errorToast from '../Toasts/ErrorToast'
import classes from './index.module.css'

/**
 * Resynchronize this artist, from the artist's own page. Internal staff only.
 *
 * The source has to be a YouTube channel id, which FAST holds only as the
 * artist's YouTube social link - and only when that link is a /channel/UC…
 * one. When it is a handle or a vanity URL there is nothing to send, so the
 * button does not appear rather than failing at the end.
 */
export function artistChannelId(artist) {
  const socials = (artist && artist.social_media) || []
  const youtube = socials.find(s => s && /youtube/i.test(s.f_id || ''))
  if (!youtube || !youtube.url) return null
  const found = channelIdFrom(youtube.url)
  return found.ok ? found.channelId : null
}

export default function RefreshArtistButton({ artist, className }) {
  const { isInternal } = useAdminAccess()
  const { state, submit, reset } = useIngestRequest()
  const [confirming, setConfirming] = useState(false)

  const channelId = artistChannelId(artist)
  if (!isInternal || !channelId) return null

  const artistName = artist && artist.name

  function cancel() {
    setConfirming(false)
    reset()
  }

  async function confirm() {
    const result = await submit(channelId, { forceRefresh: true })
    if (result.ok) {
      setConfirming(false)
      reset()
      successToast(
        result.reused
          ? // The server joins the open request and IGNORES force_refresh
            // (store.js), so promising a fresh fetch here would be a lie.
            `${artistName || 'This artist'} is already being synced. Follow it on the Ingest page.`
          : `${artistName || 'This artist'} has been sent for a fresh sync. Follow it on the Ingest page.`
      )
    } else if (result.reason === 'api_error') {
      errorToast(result.message || `We could not start that for ${artistName || 'this artist'}.`)
    }
  }

  return (
    <>
      <span className={classes.anchor}>
        <ButtonOutline
          color="pink"
          size="small"
          text="REQUEST SYNC"
          className={`${classes.matchIconButton} ${className || ''}`.trim()}
          onClick={() => setConfirming(true)}
        />
      </span>

      <ModalWindow open={confirming} onClose={cancel} disableCloseIcon={true}>
        <RefreshArtistConfirm
          artistName={artistName}
          state={state}
          onConfirm={confirm}
          onCancel={cancel}
        />
      </ModalWindow>
    </>
  )
}

RefreshArtistButton.propTypes = {
  artist: PropTypes.shape({
    name: PropTypes.string,
    social_media: PropTypes.array,
  }),
  className: PropTypes.string,
}
